import {
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
  Quality,
  canEncodeVideo,
} from 'mediabunny';

import { TranslatableError } from '@/i18n/errors';
import type { MessageKey, Params } from '@/i18n/types';
import type { Photo } from '@/lib/api';
import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from '@/lib/encode';

/**
 * Builds the timelapse in the browser.
 *
 * Rendering on the device keeps a year of someone's face off the server: the
 * frames are already on their machine behind signed URLs, and the only thing
 * the backend would add is a copy of them it does not need. The server path
 * exists for browsers without WebCodecs and for links that outlive the tab.
 */

export type TimelapseRange = 'week' | 'month' | 'year' | 'all';

/** How far back each range reaches, counting today. Mirrors the API. */
const RANGE_DAYS: Record<Exclude<TimelapseRange, 'all'>, number> = {
  week: 7,
  month: 30,
  year: 365,
};

const CODEC = 'avc';

/**
 * `preferBitrate` is load-bearing, not a tuning knob.
 *
 * A bare quality level lets mediabunny pick quantizer-driven encoding for H.264,
 * and its first candidate config then carries `bitrateMode: 'quantizer'` — a
 * value WebKit's `VideoEncoderConfig` enum does not have. Feeding it to
 * `isConfigSupported` fails WebIDL conversion, which surfaces on every iPhone as
 * a `TypeError` whose entire message is "Type error", and the bitrate candidate
 * that WebKit would have accepted is never reached. Mapping the level to a
 * bitrate keeps the config inside the enum every browser implements.
 */
const QUALITY = new Quality({ quality: 'high', preferBitrate: true });

let support: Promise<boolean> | null = null;

/**
 * Whether this browser can encode H.264 at the frame size we use.
 *
 * Asked of the encoder rather than inferred from `'VideoEncoder' in window`:
 * the API can exist while the specific codec and resolution are unsupported.
 * The probe must name the same `QUALITY` the render uses — mediabunny derives
 * the rate control from it, so probing without one asks about a configuration
 * the app never encodes with, and a browser can answer yes to that and no to
 * the real thing.
 */
export function canRenderLocally(): Promise<boolean> {
  support ??= (async () => {
    if (typeof VideoEncoder === 'undefined') {
      return false;
    }

    try {
      return await canEncodeVideo(CODEC, {
        width: OUTPUT_WIDTH,
        height: OUTPUT_HEIGHT,
        quality: QUALITY,
      });
    } catch {
      return false;
    }
  })();

  return support;
}

/** Photos that fall inside a range, oldest first. */
export function selectRange(
  photos: readonly Photo[],
  range: TimelapseRange,
  today: string,
): Photo[] {
  const oldestFirst = [...photos].sort((a, b) => a.date.localeCompare(b.date));

  if (range === 'all') {
    return oldestFirst.filter((photo) => photo.date <= today);
  }

  const from = new Date(`${today}T00:00:00Z`);
  from.setUTCDate(from.getUTCDate() - (RANGE_DAYS[range] - 1));
  const cutoff = from.toISOString().slice(0, 10);

  // String comparison is exact for ISO dates and avoids constructing a Date per
  // photo, which is the sort of thing that goes wrong across a DST boundary.
  return oldestFirst.filter((photo) => photo.date >= cutoff && photo.date <= today);
}

export interface RenderOptions {
  fps?: number;
  onProgress?: (completed: number, total: number) => void;
  signal?: AbortSignal;
}

export class TimelapseError extends TranslatableError {
  constructor(key: MessageKey, params?: Params) {
    super(key, params);
    this.name = 'TimelapseError';
  }
}

async function loadFrame(photo: Photo): Promise<ImageBitmap> {
  if (!photo.url) {
    throw new TimelapseError('errors.photo_not_ready', { date: photo.date });
  }

  // Fetched rather than assigned to an <img>: the bytes then belong to this
  // origin, so drawing them does not taint the canvas the encoder reads back.
  const response = await fetch(photo.url, { credentials: 'omit' });

  if (!response.ok) {
    throw new TimelapseError('errors.photo_load_failed', { date: photo.date });
  }

  return createImageBitmap(await response.blob());
}

/**
 * Encodes the given photos, oldest first, into an MP4.
 */
export async function renderTimelapse(
  photos: readonly Photo[],
  { fps = 10, onProgress, signal }: RenderOptions = {},
): Promise<Blob> {
  if (photos.length === 0) {
    throw new TimelapseError('errors.range_empty');
  }

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new TimelapseError('errors.canvas_context');
  }

  const output = new Output({
    format: new Mp4OutputFormat(),
    target: new BufferTarget(),
  });

  const source = new CanvasSource(canvas, {
    codec: CODEC,
    quality: QUALITY,
    // Every frame is already the same size, but a photo from before the fixed
    // output frame existed would otherwise abort the whole render.
    sizeChangeBehavior: 'cover',
  });

  output.addVideoTrack(source, { frameRate: fps });
  await output.start();

  try {
    for (const [index, photo] of photos.entries()) {
      signal?.throwIfAborted();

      const bitmap = await loadFrame(photo);

      context.drawImage(bitmap, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      bitmap.close();

      // Awaited so encoder and writer backpressure is respected; without it a
      // year of frames is decoded into memory faster than it can be encoded.
      await source.add(index / fps, 1 / fps);

      onProgress?.(index + 1, photos.length);
    }

    await output.finalize();

    const buffer = output.target.buffer;

    if (!buffer) {
      throw new TimelapseError('errors.encoder_empty');
    }

    return new Blob([buffer], { type: 'video/mp4' });
  } catch (cause) {
    // Frees the encoder and any frames still queued inside it.
    await output.cancel().catch(() => undefined);

    throw cause;
  }
}
